import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import {
  SwapFormNormalizedData,
  SwapMode,
  UseDebouncedDialogFormValuesResult,
  UseSwapFormFieldsResults,
  UseSwapFormFieldsArgs,
  UseDebouncedFormValuesArgs,
} from './types'
import { assert, raise } from '@/utils/assert'
import BigNumber from 'bignumber.js'
import { SwapFormSchema } from './validators'
import { UseFormReturn } from 'react-hook-form'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useDebounce } from '@/utils/useDebounce'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { Reserve } from '@/domain/market-info/marketInfo'
import { formFormat } from '@/domain/common/format'
import { Address } from 'viem'
import { useRouter } from 'next/navigation'
import { sortByDecreasingBalances } from '@/features/easy-borrow/logic/assets'
import { Token } from '@/domain/types/Token'

export function getSwapMaxValue({
  balance,
  isNativeAsset,
  minRemainingNativeAsset,
}: {
  balance: NormalizedUnitNumber
  isNativeAsset: boolean
  minRemainingNativeAsset: NormalizedUnitNumber
}): NormalizedUnitNumber {
  const balanceBasedMaxDeposit = balance.minus(isNativeAsset ? minRemainingNativeAsset : NormalizedUnitNumber(0))

  return NormalizedUnitNumber(BigNumber.max(balanceBasedMaxDeposit, 0))
}

export function normalizeFormValues(values: SwapFormSchema, formAssets: TokenWithBalance[]): SwapFormNormalizedData {
  function normalizeAsset(assetInput: { symbol: string; value: string }): TokenWithValue {
    const { token } = formAssets.find(({ token }) => token.symbol === assetInput.symbol) ?? raise('Asset not found')
    return {
      token,
      value: NormalizedUnitNumber(assetInput.value === '' ? '0' : assetInput.value),
    }
  }

  return {
    from: normalizeAsset(values.from),
    to: normalizeAsset(values.to),
  }
}

export function useDebouncedDialogFormValues({
  form,
  swappableAssets,
}: UseDebouncedFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = normalizeFormValues(form.watch(), swappableAssets)
  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getNormalizedFormValuesKey(formValues) + isFormValid.toString(),
  )

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

export function getNormalizedFormValuesKey(values: SwapFormNormalizedData): string {
  return [
    values.from.token.address,
    values.from.value.toFixed(),
    values.to.token.address,
    values.to.value.toFixed(),
  ].join('-')
}

export function getSwapMode(
  form: UseFormReturn<SwapFormSchema>,
  nativeAssetSymbol: TokenSymbol,
  wrappedNativeAssetSymbol: TokenSymbol,
): SwapMode {
  const fromSymbol = form.getValues('from.symbol')
  const toSymbol = form.getValues('to.symbol')

  switch (true) {
    case fromSymbol === nativeAssetSymbol && toSymbol === wrappedNativeAssetSymbol:
      return 'wrap'
    case fromSymbol === wrappedNativeAssetSymbol && toSymbol === nativeAssetSymbol:
      return 'unwrap'
    default:
      return 'swap'
  }
}

export function swapTokens(form: UseFormReturn<SwapFormSchema>): void {
  const currentFromSymbol = form.getValues('from.symbol')
  const currentFromValue = form.getValues('from.value')
  const currentToSymbol = form.getValues('to.symbol')

  form.setValue('from', { symbol: currentToSymbol, value: currentFromValue }, { shouldValidate: true })
  form.setValue('to', { symbol: currentFromSymbol, value: '' }, { shouldValidate: true })
  form.setValue('isMaxSelected', false, { shouldValidate: true })
}

export function getSwappableAssets(
  reserves: Reserve[],
  walletInfo: MarketWalletInfo,
  routerTokens: TokenWithBalance[],
): TokenWithBalance[] {
  const reservesWithBalance = reserves.map((r) => ({
    token: r.token,
    balance: walletInfo.findWalletBalanceForToken(r.token),
  }))

  const filteredRouterTokens = routerTokens.filter(
    (t) => !reservesWithBalance.some((r) => r.token.symbol.toLowerCase() === t.token.symbol.toLowerCase()),
  )

  const sortedAssets = sortByDecreasingBalances([...reservesWithBalance, ...filteredRouterTokens])

  return sortedAssets
}

export function getDefaultFormValues(
  availableTokens: TokenWithBalance[],
  {
    paramFromToken,
    paramToToken,
    toTokens,
  }: { paramFromToken: string | null; paramToToken: string | null; toTokens?: Token[] },
): SwapFormSchema {
  assert(availableTokens.length >= 2, 'At least two tokens are required for swap')

  const defaultFromToken = getDefaultFromToken(availableTokens, paramFromToken, toTokens)
  const defaultToToken = getDefaultToToken(availableTokens, paramToToken, toTokens)

  return {
    from: { symbol: defaultFromToken.token.symbol, value: '' },
    to: { symbol: defaultToToken.token.symbol, value: '' },
    isMaxSelected: false,
  }
}

function getDefaultFromToken(
  availableTokens: TokenWithBalance[],
  paramFromToken: string | null,
  toTokens?: Token[],
): TokenWithBalance {
  const filteredAvailableTokens = toTokens
    ? availableTokens.filter((t) => !toTokens.some((t2) => t2.symbol === t.token.symbol))
    : availableTokens
  const fallbackToken = filteredAvailableTokens[0]!

  if (paramFromToken) {
    return (
      filteredAvailableTokens.find((t) => t.token.symbol.toLowerCase() === paramFromToken.toLowerCase()) ||
      fallbackToken
    )
  }

  return filteredAvailableTokens.find((t) => t.token.symbol === 'HYPE') || fallbackToken
}

function getDefaultToToken(
  availableTokens: TokenWithBalance[],
  paramToToken: string | null,
  toTokens?: Token[],
): TokenWithBalance {
  const fallbackToken = availableTokens[1]!

  if (toTokens) {
    return availableTokens.find((t) => toTokens.some((t2) => t2.symbol === t.token.symbol)) || fallbackToken
  }

  if (paramToToken) {
    return availableTokens.find((t) => t.token.symbol.toLowerCase() === paramToToken.toLowerCase()) || fallbackToken
  }

  return availableTokens.find((t) => t.token.symbol === 'USDXL') || fallbackToken
}

export function useSwapFormFields({
  form,
  assets,
  fieldName,
  counterpartFieldName,
  marketInfo,
  isOnSwapPage,
  filterZeroBalance,
  toTokens,
  onChange,
  resetErrors,
  setSurgeAcknowledged,
}: UseSwapFormFieldsArgs): UseSwapFormFieldsResults {
  const router = useRouter()
  const formValues = form.getValues()
  const currentSymbol = formValues[fieldName].symbol
  const currentValue = formValues[fieldName].value
  const counterpartSymbol = formValues[counterpartFieldName].symbol

  const selectedAsset = assets.find(({ token }) => token.symbol === currentSymbol)
  assert(selectedAsset, `Selected asset not found: ${currentSymbol}`)

  function changeToken(newSymbol: TokenSymbol): void {
    // If the selected token is the same as the counterpart token, swap them
    // This is how Uniswap handles it
    form.setValue('isMaxSelected', false, { shouldValidate: false })
    setSurgeAcknowledged(false)
    resetErrors()

    if (newSymbol === counterpartSymbol) {
      if (isOnSwapPage) {
        router.replace(`/swap?from=${form.getValues('to.symbol')}&to=${form.getValues('from.symbol')}`, {
          scroll: false,
        })
      }

      swapTokens(form)
      return
    }

    if (isOnSwapPage) {
      router.replace(`/swap?${fieldName}=${newSymbol}&${counterpartFieldName}=${counterpartSymbol}`, {
        scroll: false,
      })
    }

    form.setValue(fieldName, { symbol: newSymbol, value: currentValue }, { shouldValidate: true })
    form.setValue(counterpartFieldName, { symbol: counterpartSymbol, value: '' }, { shouldValidate: true })
  }

  const availableAssets = getAvailableAssets({
    assets,
    toTokens,
    fromSymbol: formValues.from.symbol,
    isToField: fieldName === 'to',
  })

  const maxValue =
    fieldName === 'from'
      ? getSwapMaxValue({
          balance: selectedAsset.balance,
          isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetSymbol === currentSymbol,
          minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
        })
      : NormalizedUnitNumber(0)

  return {
    selectedAsset,
    availableAssets:
      filterZeroBalance && fieldName === 'from'
        ? availableAssets.filter(({ balance }) => balance.gt(0))
        : availableAssets,
    onChange,
    changeToken,
    maxSelectedFieldName: fieldName === 'from' ? 'isMaxSelected' : undefined,
    maxValue,
    onSetMax:
      fieldName === 'from'
        ? (max: NormalizedUnitNumber) => {
            form.setValue('from.value', formFormat(max, selectedAsset.token.decimals))
            resetErrors()
          }
        : undefined,
  }
}

function getAvailableAssets({
  assets,
  toTokens,
  fromSymbol,
  isToField,
}: {
  assets: TokenWithBalance[]
  toTokens?: Token[]
  fromSymbol: TokenSymbol
  isToField: boolean
}): TokenWithBalance[] {
  if (isToField) {
    return toTokens
      ? assets.filter(({ token }) => toTokens.some((t) => t.symbol === token.symbol))
      : assets.filter(({ token }) => token.symbol.toLowerCase() !== fromSymbol.toLowerCase())
  }

  return toTokens ? assets.filter(({ token }) => !toTokens.some((t) => t.symbol === token.symbol)) : assets
}

export function hypeAddressOverride(address: Address): Address {
  return address === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
    ? '0x2222222222222222222222222222222222222222'
    : address
}
